// Slice 2C5A · Guided document-intake foundation (frontend schema layer).
//
// This module owns the CANONICAL question schema per family, the answer
// reducers, the wording-source lifecycle constants, and the answer-change
// reconciliation helper. Slice 2C5A wires PROPOSAL only; the other four
// families are declared here at empty-schema level so the same panel can
// render them in later slices without a parallel system.

// --- Wording-source lifecycle (matches backend) ------------------------------
export const WORDING_SOURCES = Object.freeze([
  "generated", "customized", "legacy", "outside_jobclose_ai", "later",
]);

export const ACTIONS = Object.freeze([
  "use_recommended", "save_customized",
  "use_existing", "handle_outside", "set_later",
]);

// Which four action buttons are exposed on the guided panel for PROPOSAL.
// (`handle_outside` is reserved for Closeout in Slice 2C5C.)
export const PROPOSAL_PRIMARY_ACTIONS = Object.freeze([
  "use_recommended", "edit_wording", "help_me_change_it", "set_later",
]);

// --- Question schema · PROPOSAL ---------------------------------------------
// Every question drives the recommended-wording preview live. Answers are
// stored in `answers` on the companion record. Conditional inputs (percent
// amount, custom text, flat amount) only render when the parent choice is
// active.
export const PROPOSAL_QUESTIONS = Object.freeze([
  {
    id: "tone", key: "tone", kind: "chip",
    prompt: "How should your proposal introduction sound?",
    choices: [
      { value: "friendly",        label: "Friendly" },
      { value: "professional",    label: "Professional" },
      { value: "straightforward", label: "Straightforward" },
    ],
    defaultValue: "professional",
  },
  {
    id: "deposit", key: "deposit_kind", kind: "chip",
    prompt: "Do you require a deposit?",
    choices: [
      { value: "none",    label: "No deposit" },
      { value: "percent", label: "Percentage" },
      { value: "fixed",   label: "Fixed dollar amount" },
    ],
    defaultValue: "none",
    conditionalInputs: [
      { when: "percent", key: "deposit_percent", kind: "number",
        label: "Deposit percentage", min: 1, max: 100, suffix: "%" },
      { when: "fixed",   key: "deposit_amount",  kind: "number",
        label: "Deposit amount",     min: 0,          prefix: "$" },
    ],
    conditionalChip: {
      appearWhen: (kind) => kind === "percent" || kind === "fixed",
      key: "deposit_due", prompt: "When is the deposit due?",
      choices: [
        { value: "on_approval",      label: "When the proposal is approved" },
        { value: "before_materials", label: "Before materials are ordered" },
        { value: "before_work",      label: "Before work begins" },
      ],
      defaultValue: "on_approval",
    },
  },
  {
    id: "final_due", key: "final_due", kind: "chip",
    prompt: "When is the remaining balance due?",
    choices: [
      { value: "on_completion", label: "When work is complete" },
      { value: "net_7",         label: "Within 7 days" },
      { value: "net_15",        label: "Within 15 days" },
      { value: "net_30",        label: "Within 30 days" },
      { value: "custom",        label: "Custom" },
    ],
    defaultValue: "on_completion",
    conditionalInputs: [
      { when: "custom", key: "final_due_custom", kind: "text",
        label: "Describe the timing", placeholder: "e.g. within 10 business days" },
    ],
  },
  {
    id: "late_fee", key: "late_fee", kind: "chip",
    prompt: "Do you charge a late fee?",
    choices: [
      { value: "none",                       label: "No late fee" },
      { value: "one_point_five_percent",     label: "1.5% per month" },
      { value: "flat",                       label: "Flat dollar amount" },
      { value: "custom",                     label: "Custom" },
    ],
    defaultValue: "none",
    conditionalInputs: [
      { when: "flat",   key: "late_fee_amount", kind: "number",
        label: "Flat late fee",  min: 0, prefix: "$" },
      { when: "custom", key: "late_fee_custom", kind: "text",
        label: "Describe your late-fee policy",
        placeholder: "e.g. 5% of the outstanding balance per month" },
    ],
  },
  {
    id: "notes", key: "notes", kind: "checkbox_group",
    prompt: "Which standard proposal notes should be included?",
    choices: [
      { value: "notes_change_order",       label: "Written change-order approval" },
      { value: "notes_warranty_separate",  label: "Warranty information is provided separately" },
    ],
    noneLabel: "None",
  },
]);

// --- Question schema · CONTRACT (Slice 2C5B) ---------------------------------
// Six compact, practical questions. Two multi-select checkbox groups
// (`jobsite_access`, `material_selections`) require the same array-of-
// enumerated-strings shape enforced by the backend. `jobsite_access`
// treats the `none_specific` value as mutually exclusive with the four
// other options.
export const CONTRACT_JOBSITE_ACCESS_VALUES = Object.freeze([
  "property_access", "utilities", "pets", "valuables", "none_specific",
]);
export const CONTRACT_MATERIAL_SELECTION_VALUES = Object.freeze([
  "customer_approval", "special_order_nonreturnable",
  "substitutions_need_approval",
]);
export const CONTRACT_QUESTIONS = Object.freeze([
  {
    id: "tone", key: "tone", kind: "chip",
    prompt: "How should your contract sound?",
    choices: [
      { value: "friendly",        label: "Friendly" },
      { value: "professional",    label: "Professional" },
      { value: "straightforward", label: "Straightforward" },
    ],
    defaultValue: "professional",
  },
  {
    id: "permits", key: "permits", kind: "chip",
    prompt: "Who usually handles permits?",
    choices: [
      { value: "contractor",   label: "Contractor" },
      { value: "customer",     label: "Customer" },
      { value: "depends",      label: "Depends on the job" },
      { value: "not_required", label: "Permits usually are not required" },
    ],
    defaultValue: "contractor",
  },
  {
    id: "concealed_conditions", key: "concealed_conditions", kind: "chip",
    prompt: "How should unexpected or hidden conditions be handled?",
    choices: [
      { value: "change_order",     label: "Stop and get written change-order approval before extra work" },
      { value: "emergency_notify", label: "Allow emergency safety work and notify the customer" },
      { value: "help_me_decide",   label: "Help me decide" },
    ],
    defaultValue: "change_order",
  },
  {
    id: "jobsite_access", key: "jobsite_access", kind: "checkbox_multiselect",
    prompt: "What must the customer normally provide at the jobsite?",
    choices: [
      { value: "property_access", label: "Access to the property" },
      { value: "utilities",       label: "Electricity and water" },
      { value: "pets",            label: "Secured pets" },
      { value: "valuables",       label: "Removal or protection of valuables" },
      { value: "none_specific",   label: "Nothing specific by default" },
    ],
    // Slice 2C5B · Q3 · "Nothing specific" cannot coexist with any other option.
    mutuallyExclusive: "none_specific",
    defaultValue: [],
  },
  {
    id: "scheduling", key: "scheduling", kind: "chip",
    prompt: "How should scheduling be described?",
    choices: [
      { value: "estimated_may_change",  label: "Estimated dates may change because of weather, materials, inspections, or conditions outside the contractor's control" },
      { value: "firm_unless_written",   label: "Firm dates unless changed in writing" },
      { value: "help_me_decide",        label: "Help me decide" },
    ],
    defaultValue: "estimated_may_change",
  },
  {
    id: "material_selections", key: "material_selections", kind: "checkbox_multiselect",
    prompt: "How should materials, colors, and product selections be handled?",
    choices: [
      { value: "customer_approval",           label: "Customer approves selections before ordering" },
      { value: "special_order_nonreturnable", label: "Special-order materials may be nonreturnable" },
      { value: "substitutions_need_approval", label: "Comparable substitutions require customer approval" },
    ],
    defaultValue: [
      "customer_approval",
      "special_order_nonreturnable",
      "substitutions_need_approval",
    ],
  },
  {
    id: "cleanup", key: "cleanup", kind: "chip",
    prompt: "What cleanup is normally included?",
    choices: [
      { value: "daily_and_final", label: "Reasonable daily cleanup and final debris removal" },
      { value: "final_only",      label: "Final cleanup only" },
      { value: "disposal_billed", label: "Disposal is billed separately when required" },
      { value: "depends",         label: "Depends on the job" },
    ],
    defaultValue: "daily_and_final",
  },
]);

// --- Question schema · INVOICE (Slice 2C5B Pass 2) ---------------------------
// Six practical questions. Two multi-selects (`payment_methods`,
// `footer_items`). Card-fee (Q4) is conditional on Q3 including "card".
// Retainage is conditional on Q1 == "progress_milestone".
export const INVOICE_PAYMENT_METHOD_VALUES = Object.freeze([
  "check", "cash", "card", "ach", "financing", "other",
]);
export const INVOICE_FOOTER_ITEM_VALUES = Object.freeze([
  "thank_customer", "contact_billing",
  "show_prior_payments", "show_remaining_balance",
  "payment_instructions", "license_info",
]);
export const INVOICE_QUESTIONS = Object.freeze([
  {
    id: "tone", key: "tone", kind: "chip",
    prompt: "How should your invoices sound?",
    choices: [
      { value: "friendly",        label: "Friendly" },
      { value: "professional",    label: "Professional" },
      { value: "straightforward", label: "Straightforward" },
    ],
    defaultValue: "professional",
  },
  {
    id: "billing_approach", key: "billing_approach", kind: "chip",
    prompt: "How do you usually bill customers?",
    choices: [
      { value: "one_final",          label: "One final invoice" },
      { value: "deposit_then_final", label: "Deposit, then final invoice" },
      { value: "progress_milestone", label: "Progress or milestone invoices" },
      { value: "depends",            label: "Depends on the job" },
    ],
    defaultValue: "one_final",
  },
  {
    id: "payment_due", key: "payment_due", kind: "chip",
    prompt: "When is payment normally due?",
    choices: [
      { value: "upon_receipt",       label: "Due upon receipt" },
      { value: "on_completion",      label: "Due when work is completed" },
      { value: "net_7",              label: "Within 7 days" },
      { value: "net_15",             label: "Within 15 days" },
      { value: "net_30",             label: "Within 30 days" },
      { value: "use_approved_terms", label: "Use the approved Proposal or Contract terms" },
    ],
    defaultValue: "on_completion",
  },
  {
    id: "payment_methods", key: "payment_methods", kind: "checkbox_multiselect",
    prompt: "How can customers pay you?",
    choices: [
      { value: "check",     label: "Check" },
      { value: "cash",      label: "Cash" },
      { value: "card",      label: "Credit or debit card" },
      { value: "ach",       label: "ACH or bank transfer" },
      { value: "financing", label: "Financing" },
      { value: "other",     label: "Other" },
    ],
    defaultValue: ["check"],
    // At least one payment method must remain selected before saving.
    minSelected: 1,
  },
  {
    id: "card_fee_handling", key: "card_fee_handling", kind: "chip",
    prompt: "How should card or processing fees be handled?",
    // Q4 only appears when Q3 (payment_methods) includes "card".
    dependsOn: {
      key: "payment_methods",
      predicate: (methods) => Array.isArray(methods) && methods.includes("card"),
    },
    choices: [
      { value: "absorb",        label: "We absorb the fee" },
      { value: "add_disclosed", label: "Add a disclosed processing fee where permitted" },
      { value: "no_cards",      label: "We do not accept card payments" },
      { value: "per_job",       label: "Decide per job" },
    ],
    defaultValue: "absorb",
  },
  {
    id: "overdue_handling", key: "overdue_handling", kind: "chip",
    prompt: "How should overdue balances be handled?",
    choices: [
      { value: "use_approved_terms",     label: "Use the approved late-payment terms" },
      { value: "friendly_reminder_only", label: "Send a friendly payment reminder only" },
      { value: "no_late_fee",            label: "No late fee" },
      { value: "help_me_decide",         label: "Help me decide" },
    ],
    defaultValue: "use_approved_terms",
  },
  {
    id: "footer_items", key: "footer_items", kind: "checkbox_multiselect",
    prompt: "What should normally appear at the bottom of invoices?",
    choices: [
      { value: "thank_customer",         label: "Thank the customer" },
      { value: "contact_billing",        label: "Ask the customer to contact us with billing questions" },
      { value: "show_prior_payments",    label: "Show payments already received" },
      { value: "show_remaining_balance", label: "Show the remaining balance clearly" },
      { value: "payment_instructions",   label: "Include payment instructions" },
      { value: "license_info",           label: "Include license or registration information" },
    ],
    defaultValue: [
      "thank_customer", "contact_billing",
      "show_prior_payments", "show_remaining_balance",
      "payment_instructions",
    ],
  },
  {
    // Conditional retainage question — appears only when billing_approach
    // is progress_milestone. Answers stay off the payload otherwise.
    id: "retainage_kind", key: "retainage_kind", kind: "chip",
    prompt: "Do you normally hold retainage?",
    dependsOn: {
      key: "billing_approach",
      predicate: (v) => v === "progress_milestone",
    },
    choices: [
      { value: "none",    label: "No retainage" },
      { value: "percent", label: "Yes, enter a percentage" },
      { value: "depends", label: "Depends on the project" },
    ],
    defaultValue: "none",
    conditionalInputs: [
      { when: "percent", key: "retainage_percent", kind: "number",
        label: "Retainage percentage", min: 1, max: 100, suffix: "%" },
    ],
  },
]);

// --- Question schema · CUSTOMER UPDATE (Slice 2C5B Pass 3) -------------------
// Six practical questions. Two multi-selects (`milestones`, `share_items`).
// `share_items='nothing_automatic'` is mutually exclusive with the other
// three approved-content options — enforced client-side by
// toggleMultiSelect's `mutuallyExclusive` option.
export const CUSTOMER_UPDATE_MILESTONE_VALUES = Object.freeze([
  "job_scheduled", "work_started", "delay_or_schedule_change",
  "inspection_update", "customer_decision_needed",
  "change_order_update", "work_completed",
  "invoice_or_payment_reminder", "other",
]);
export const CUSTOMER_UPDATE_SHARE_ITEM_VALUES = Object.freeze([
  "approved_photos", "approved_schedule", "approved_documents",
  "nothing_automatic",
]);
export const CUSTOMER_UPDATE_QUESTIONS = Object.freeze([
  {
    id: "tone", key: "tone", kind: "chip",
    prompt: "How should customer updates sound?",
    choices: [
      { value: "friendly",        label: "Friendly" },
      { value: "professional",    label: "Professional" },
      { value: "straightforward", label: "Straightforward" },
    ],
    defaultValue: "professional",
  },
  {
    id: "milestones", key: "milestones", kind: "checkbox_multiselect",
    prompt: "Which milestones should prepare an update draft?",
    // Selected milestones mean "prepare a draft for contractor review"
    // — never automatic send/share/notify.
    helpText: "Selected milestones prepare a draft for contractor review. Nothing is sent automatically.",
    choices: [
      { value: "job_scheduled",              label: "Job scheduled" },
      { value: "work_started",               label: "Work started" },
      { value: "delay_or_schedule_change",   label: "Delay or schedule change" },
      { value: "inspection_update",          label: "Inspection update" },
      { value: "customer_decision_needed",   label: "Customer decision needed" },
      { value: "change_order_update",        label: "Change order update" },
      { value: "work_completed",             label: "Work completed" },
      { value: "invoice_or_payment_reminder", label: "Invoice or payment reminder" },
      { value: "other",                       label: "Other" },
    ],
    defaultValue: [
      "job_scheduled", "delay_or_schedule_change",
      "customer_decision_needed", "change_order_update",
      "work_completed", "invoice_or_payment_reminder",
    ],
    minSelected: 1,
  },
  {
    id: "delivery_method", key: "delivery_method", kind: "chip",
    prompt: "How should updates usually be delivered after you approve them?",
    helpText: "This is a future delivery preference only. JobClose AI does not send updates.",
    choices: [
      { value: "text",             label: "Text message" },
      { value: "email",            label: "Email" },
      { value: "text_and_email",   label: "Text and email" },
      { value: "customer_portal",  label: "Customer portal" },
      { value: "decide_each",      label: "Decide for each update" },
    ],
    defaultValue: "text_and_email",
  },
  {
    id: "recipient_rule", key: "recipient_rule", kind: "chip",
    prompt: "Who should normally receive customer updates?",
    choices: [
      { value: "primary",       label: "Primary customer contact" },
      { value: "all_approved",  label: "All approved customer contacts" },
      { value: "choose_each",   label: "Choose for each update" },
    ],
    defaultValue: "primary",
  },
  {
    id: "language_rule", key: "language_rule", kind: "chip",
    prompt: "What language should JobClose AI use for update drafts?",
    // UI-only note when language resolves dynamically at job time.
    helpText: "For 'Customer's preferred language' or 'Choose for each update', the actual draft language is resolved at job time. This note is UI-only and is not saved as customer-facing wording.",
    choices: [
      { value: "preferred",   label: "Customer's preferred language" },
      { value: "en",          label: "English" },
      { value: "es",          label: "Spanish" },
      { value: "choose_each", label: "Choose for each update" },
    ],
    defaultValue: "preferred",
  },
  {
    id: "share_items", key: "share_items", kind: "checkbox_multiselect",
    prompt: "What can JobClose AI include in a customer-update draft?",
    helpText: "Only contractor-approved items may ever be included. The contractor still reviews and approves every send/share action.",
    choices: [
      { value: "approved_photos",    label: "Approved job photos" },
      { value: "approved_schedule",  label: "Approved schedule or timing" },
      { value: "approved_documents", label: "Approved customer-facing documents" },
      { value: "nothing_automatic",  label: "Nothing automatically attached or linked" },
    ],
    // nothing_automatic collapses the array to just itself and clears when
    // any other option is picked — enforced by toggleMultiSelect below.
    mutuallyExclusive: "nothing_automatic",
    defaultValue: ["nothing_automatic"],
  },
]);

// Families whose guided schema lands in later slices — declared here so the
// panel can render Proposal without importing family-specific code.
// --- Question schema · CLOSEOUT (Slice 2C5C Pass 1) --------------------------
export const CLOSEOUT_PACKAGE_ITEM_VALUES = Object.freeze([
  "final_invoice_summary", "signed_contract_and_change_orders",
  "workmanship_warranty", "manufacturer_warranty_info",
  "permit_final_inspection_docs", "approved_final_photos",
  "care_maintenance_instructions", "nothing_automatic",
]);
export const CLOSEOUT_QUESTIONS = Object.freeze([
  {
    id: "package_items", key: "package_items", kind: "checkbox_multiselect",
    prompt: "What should normally be considered for the closeout package?",
    helpText: "These are future package preferences only. Nothing is automatically attached, shared, or sent.",
    choices: [
      { value: "final_invoice_summary",              label: "Final invoice or payment summary" },
      { value: "signed_contract_and_change_orders",  label: "Signed contract and approved change orders" },
      { value: "workmanship_warranty",               label: "Workmanship warranty" },
      { value: "manufacturer_warranty_info",         label: "Manufacturer warranty information" },
      { value: "permit_final_inspection_docs",       label: "Permit or final-inspection documents" },
      { value: "approved_final_photos",              label: "Approved final job photos" },
      { value: "care_maintenance_instructions",      label: "Care or maintenance instructions" },
      { value: "nothing_automatic",                  label: "Nothing added automatically" },
    ],
    mutuallyExclusive: "nothing_automatic",
    defaultValue: [
      "final_invoice_summary", "signed_contract_and_change_orders",
      "workmanship_warranty", "approved_final_photos",
    ],
    minSelected: 1,
  },
  {
    id: "draft_trigger", key: "draft_trigger", kind: "chip",
    prompt: "When should JobClose AI prepare a closeout draft?",
    helpText: "A closeout draft is prepared only after contractor-confirmed job information exists. Calendar dates are not treated as completion.",
    choices: [
      { value: "after_inspections", label: "After work and required inspections are confirmed complete" },
      { value: "after_walkthrough", label: "After the final walkthrough or customer acceptance" },
      { value: "after_punchlist",   label: "After all punch-list items are complete" },
      { value: "decide_each",       label: "Decide for each job" },
    ],
    defaultValue: "after_inspections",
  },
  {
    id: "walkthrough_rule", key: "walkthrough_rule", kind: "chip",
    prompt: "How do you normally handle the final walkthrough?",
    choices: [
      { value: "always",        label: "Always complete a final walkthrough" },
      { value: "when_required", label: "Complete one when the job requires it" },
      { value: "none",          label: "No formal walkthrough" },
      { value: "decide_each",   label: "Decide for each job" },
    ],
    defaultValue: "when_required",
  },
  {
    id: "punch_list_rule", key: "punch_list_rule", kind: "chip",
    prompt: "How should punch-list items be handled?",
    helpText: "Wording never implies incomplete work is complete.",
    choices: [
      { value: "all_before_closeout", label: "Complete all punch-list items before closeout" },
      { value: "written_after_ok",    label: "Allow a customer-approved written punch list after closeout" },
      { value: "none",                label: "No formal punch-list process" },
      { value: "decide_each",         label: "Decide for each job" },
    ],
    defaultValue: "all_before_closeout",
  },
  {
    id: "acknowledgment_rule", key: "acknowledgment_rule", kind: "chip",
    prompt: "How should the customer acknowledge completion?",
    helpText: "This is a future preference only. No signature collection is built in this pass.",
    choices: [
      { value: "signature",   label: "Customer signature" },
      { value: "written",     label: "Written acknowledgment" },
      { value: "optional",    label: "Optional acknowledgment" },
      { value: "decide_each", label: "Decide for each job" },
    ],
    defaultValue: "written",
  },
  {
    id: "photo_rule", key: "photo_rule", kind: "chip",
    prompt: "How should final photos be handled?",
    helpText: "Only contractor-approved photos may ever be referenced.",
    choices: [
      { value: "before_and_after", label: "Approved before-and-after photos" },
      { value: "final_only",       label: "Approved final photos only" },
      { value: "none",             label: "No photos by default" },
      { value: "decide_each",      label: "Decide for each job" },
    ],
    defaultValue: "final_only",
  },
  {
    id: "tone", key: "tone", kind: "chip",
    prompt: "How should closeout wording sound?",
    choices: [
      { value: "friendly",        label: "Friendly" },
      { value: "professional",    label: "Professional" },
      { value: "straightforward", label: "Straightforward" },
    ],
    defaultValue: "professional",
  },
]);

// --- Question schema · WARRANTY (Slice 2C5C Pass 2) -------------------------
// Nine practical questions plus simple trade overrides. Wording generator
// writes only into the three existing warranty document-default fields
// (workmanship_warranty_terms, manufacturer_warranty_notes,
// warranty_contact_notes). No new public fields are introduced.
export const WARRANTY_COVERAGE_VALUES = Object.freeze([
  "defects_workmanship", "repairs_covered_work", "decide_each",
]);
export const WARRANTY_EXCLUSION_VALUES = Object.freeze([
  "manufacturer_or_product_defects", "normal_wear_and_tear",
  "misuse_abuse_neglect", "weather_disasters_outside_forces",
  "altered_or_repaired_by_others", "pre_existing_or_out_of_scope",
  "decide_each",
]);
export const WARRANTY_REQUEST_INFO_VALUES = Object.freeze([
  "customer_and_project_name", "description_of_concern",
  "photos_when_available", "date_first_noticed", "decide_each",
]);
export const WARRANTY_PERIOD_VALUES = Object.freeze([
  "30_days", "90_days", "1_year", "2_years", "5_years",
  "custom", "decide_each",
]);
export const WARRANTY_PERIOD_LABELS = Object.freeze({
  "30_days":  "30 days",
  "90_days":  "90 days",
  "1_year":   "1 year",
  "2_years":  "2 years",
  "5_years":  "5 years",
  "custom":   "Custom",
  "decide_each": "Decide for each job",
});
export const WARRANTY_QUESTIONS = Object.freeze([
  {
    id: "workmanship_offered", key: "workmanship_offered", kind: "chip",
    prompt: "Do you normally provide a workmanship warranty?",
    choices: [
      { value: "yes",         label: "Yes" },
      { value: "no_default",  label: "No workmanship warranty by default" },
      { value: "decide_each", label: "Decide for each job" },
    ],
    defaultValue: "yes",
  },
  {
    id: "workmanship_period", key: "workmanship_period", kind: "chip",
    prompt: "What is your standard workmanship warranty period?",
    helpText: "Only shown when a workmanship warranty is normally offered.",
    choices: WARRANTY_PERIOD_VALUES.map((v) => ({ value: v, label: WARRANTY_PERIOD_LABELS[v] })),
    defaultValue: "1_year",
    onlyWhen: { key: "workmanship_offered", equals: "yes" },
    conditionalInputs: [
      { when: "custom", key: "custom_workmanship_period", kind: "text",
        label: "Custom warranty period",
        placeholder: "e.g. 3 years on flat-roof workmanship",
        max: 200 },
    ],
  },
  {
    id: "coverage_start", key: "coverage_start", kind: "chip",
    prompt: "When should workmanship warranty coverage begin?",
    helpText: "Calendar dates alone are not treated as completion confirmation.",
    choices: [
      { value: "contractor_substantial",    label: "Contractor-confirmed substantial completion" },
      { value: "contractor_final",          label: "Contractor-confirmed final completion" },
      { value: "customer_acknowledgment",   label: "Customer acknowledgment of completion" },
      { value: "decide_each",               label: "Decide for each job" },
    ],
    defaultValue: "contractor_final",
    onlyWhen: { key: "workmanship_offered", equals: "yes" },
  },
  {
    id: "workmanship_coverage", key: "workmanship_coverage", kind: "checkbox_multiselect",
    prompt: "What should the standard workmanship warranty cover?",
    helpText: "At least one selection is required. \"Decide coverage for each job\" is mutually exclusive with the other options.",
    choices: [
      { value: "defects_workmanship",  label: "Defects caused by our workmanship" },
      { value: "repairs_covered_work", label: "Repairs to the covered work when a valid workmanship issue is confirmed" },
      { value: "decide_each",          label: "Decide coverage for each job" },
    ],
    mutuallyExclusive: "decide_each",
    minSelected: 1,
    onlyWhen: { key: "workmanship_offered", equals: "yes" },
    defaultValue: ["defects_workmanship", "repairs_covered_work"],
  },
  {
    id: "workmanship_exclusions", key: "workmanship_exclusions", kind: "checkbox_multiselect",
    prompt: "What should normally be excluded from the workmanship warranty?",
    helpText: "At least one selection is required. \"Decide exclusions for each job\" is mutually exclusive with all other options.",
    choices: [
      { value: "manufacturer_or_product_defects",     label: "Manufacturer or product defects" },
      { value: "normal_wear_and_tear",                label: "Normal wear and tear" },
      { value: "misuse_abuse_neglect",                label: "Damage caused by misuse, abuse, neglect, or lack of maintenance" },
      { value: "weather_disasters_outside_forces",    label: "Damage caused by weather, disasters, or outside forces" },
      { value: "altered_or_repaired_by_others",       label: "Work altered or repaired by others" },
      { value: "pre_existing_or_out_of_scope",        label: "Pre-existing conditions or work outside the approved scope" },
      { value: "decide_each",                         label: "Decide exclusions for each job" },
    ],
    mutuallyExclusive: "decide_each",
    minSelected: 1,
    onlyWhen: { key: "workmanship_offered", equals: "yes" },
    defaultValue: [
      "manufacturer_or_product_defects", "normal_wear_and_tear",
      "misuse_abuse_neglect", "weather_disasters_outside_forces",
      "altered_or_repaired_by_others", "pre_existing_or_out_of_scope",
    ],
  },
  {
    id: "manufacturer_handling", key: "manufacturer_handling", kind: "chip",
    prompt: "How should manufacturer warranties be handled?",
    helpText: "Manufacturer coverage is described separately and never guaranteed.",
    choices: [
      { value: "provide_available",     label: "Provide available manufacturer warranty information when applicable" },
      { value: "refer_to_manufacturer", label: "Refer the customer directly to the manufacturer" },
      { value: "none_default",          label: "No manufacturer warranty information by default" },
      { value: "decide_each",           label: "Decide for each job" },
    ],
    defaultValue: "provide_available",
  },
  {
    id: "service_contact_rule", key: "service_contact_rule", kind: "chip",
    prompt: "How should customers request warranty service?",
    choices: [
      { value: "company_contact", label: "Contact our company using the information on the document" },
      { value: "specific",        label: "Use a specific warranty contact" },
      { value: "decide_each",     label: "Decide for each job" },
    ],
    defaultValue: "company_contact",
    conditionalInputs: [
      { when: "specific", key: "specific_service_contact", kind: "text",
        label: "Specific warranty contact",
        placeholder: "e.g. Warranty desk · (555) 555-1234 · warranty@acme.example",
        max: 500 },
    ],
  },
  {
    id: "request_information", key: "request_information", kind: "checkbox_multiselect",
    prompt: "What information should customers provide when requesting warranty service?",
    helpText: "These are customer instructions only. No claim intake workflow is built in this pass.",
    choices: [
      { value: "customer_and_project_name", label: "Customer and project name" },
      { value: "description_of_concern",    label: "Description of the concern" },
      { value: "photos_when_available",     label: "Photos of the concern when available" },
      { value: "date_first_noticed",        label: "Date the concern was first noticed" },
      { value: "decide_each",               label: "Decide for each job" },
    ],
    mutuallyExclusive: "decide_each",
    defaultValue: [
      "customer_and_project_name", "description_of_concern",
      "photos_when_available",
    ],
  },
  {
    id: "tone", key: "tone", kind: "chip",
    prompt: "How should warranty wording sound?",
    choices: [
      { value: "friendly",        label: "Friendly" },
      { value: "professional",    label: "Professional" },
      { value: "straightforward", label: "Straightforward" },
    ],
    defaultValue: "professional",
  },
]);

export const FAMILY_QUESTION_SCHEMAS = Object.freeze({
  proposal:        PROPOSAL_QUESTIONS,
  contract:        CONTRACT_QUESTIONS,
  invoice:         INVOICE_QUESTIONS,
  customer_update: CUSTOMER_UPDATE_QUESTIONS,
  closeout:        CLOSEOUT_QUESTIONS,
  warranty:        WARRANTY_QUESTIONS,
});

// --- Empty answer bag per family --------------------------------------------
export const emptyAnswersForFamily = (family) => {
  if (family === "proposal") {
    return {
      tone: "professional",
      deposit_kind: "none",
      deposit_due: "on_approval",
      final_due: "on_completion",
      late_fee: "none",
      notes_change_order: false,
      notes_warranty_separate: false,
    };
  }
  if (family === "contract") {
    return {
      tone: "professional",
      permits: "contractor",
      concealed_conditions: "change_order",
      jobsite_access: [],
      scheduling: "estimated_may_change",
      material_selections: [
        "customer_approval",
        "special_order_nonreturnable",
        "substitutions_need_approval",
      ],
      cleanup: "daily_and_final",
    };
  }
  if (family === "invoice") {
    return {
      tone: "professional",
      billing_approach: "one_final",
      payment_due: "on_completion",
      payment_methods: ["check"],
      // card_fee_handling omitted by default (Q4 hidden until card is picked)
      overdue_handling: "use_approved_terms",
      footer_items: [
        "thank_customer", "contact_billing",
        "show_prior_payments", "show_remaining_balance",
        "payment_instructions",
      ],
      // retainage_kind omitted until progress_milestone is chosen
    };
  }
  if (family === "customer_update") {
    return {
      tone: "professional",
      milestones: [
        "job_scheduled", "delay_or_schedule_change",
        "customer_decision_needed", "change_order_update",
        "work_completed", "invoice_or_payment_reminder",
      ],
      delivery_method: "text_and_email",
      recipient_rule:  "primary",
      language_rule:   "preferred",
      share_items:     ["nothing_automatic"],
    };
  }
  if (family === "closeout") {
    return {
      tone: "professional",
      package_items: [
        "final_invoice_summary", "signed_contract_and_change_orders",
        "workmanship_warranty", "approved_final_photos",
      ],
      draft_trigger:       "after_inspections",
      walkthrough_rule:    "when_required",
      punch_list_rule:     "all_before_closeout",
      acknowledgment_rule: "written",
      photo_rule:          "final_only",
    };
  }
  if (family === "warranty") {
    return {
      tone: "professional",
      workmanship_offered:  "yes",
      workmanship_period:   "1_year",
      coverage_start:       "contractor_final",
      workmanship_coverage: ["defects_workmanship", "repairs_covered_work"],
      workmanship_exclusions: [
        "manufacturer_or_product_defects", "normal_wear_and_tear",
        "misuse_abuse_neglect", "weather_disasters_outside_forces",
        "altered_or_repaired_by_others", "pre_existing_or_out_of_scope",
      ],
      manufacturer_handling: "provide_available",
      service_contact_rule:  "company_contact",
      request_information: [
        "customer_and_project_name", "description_of_concern",
        "photos_when_available",
      ],
      use_trade_overrides: false,
    };
  }
  return {};
};

// --- Answer reducer ---------------------------------------------------------
export const setAnswer = (bag, key, value) => {
  const next = { ...(bag || {}) };
  next[key] = value;
  // Drop stale conditional sub-answers when the parent chip changes.
  if (key === "deposit_kind") {
    if (value !== "percent") delete next.deposit_percent;
    if (value !== "fixed")   delete next.deposit_amount;
    if (value === "none")    delete next.deposit_due;
  }
  if (key === "final_due" && value !== "custom") delete next.final_due_custom;
  if (key === "late_fee") {
    if (value !== "flat")   delete next.late_fee_amount;
    if (value !== "custom") delete next.late_fee_custom;
  }
  // Slice 2C5B Pass 2 · Invoice stale-answer cleanup.
  //   * Changing billing_approach away from progress_milestone must
  //     strip retainage_kind + retainage_percent so they never leak into
  //     the generated recommendation or backend payload.
  //   * Changing retainage_kind away from "percent" must strip the
  //     accompanying retainage_percent.
  //   * The card-fee cleanup for payment_methods lives in toggleMultiSelect
  //     because payment_methods is a multi-select toggle path.
  if (key === "billing_approach" && value !== "progress_milestone") {
    delete next.retainage_kind;
    delete next.retainage_percent;
  }
  if (key === "retainage_kind" && value !== "percent") {
    delete next.retainage_percent;
  }
  // Slice 2C5B Pass 2 · Invoice card-payment contradiction resolution.
  //   When the contractor picks "We do not accept card payments" on
  //   Q4 (card_fee_handling), the correct product behavior is not to
  //   record a contradictory answer alongside Card being in Q3's
  //   accepted payment methods. Instead we resolve the contradiction
  //   locally: drop Card from payment_methods, delete the stale
  //   card_fee_handling answer, and — because Q4's visibility is gated
  //   on Card being in Q3 — the conditional card-fee question hides
  //   automatically. All other Invoice answers are preserved. No
  //   server write. Backend contradiction validation stays intact.
  if (key === "card_fee_handling" && value === "no_cards") {
    if (Array.isArray(next.payment_methods)) {
      next.payment_methods = next.payment_methods.filter((m) => m !== "card");
    }
    delete next.card_fee_handling;
  }
  // Slice 2C5C Pass 2 · Warranty stale-answer cleanup.
  //   * When workmanship_offered flips away from "yes", strip the
  //     workmanship_period / custom_workmanship_period / coverage_start /
  //     workmanship_coverage / workmanship_exclusions answers (the panel
  //     hides those questions in that state).
  //   * When workmanship_period flips away from "custom", strip
  //     custom_workmanship_period.
  //   * When service_contact_rule flips away from "specific", strip
  //     specific_service_contact.
  //   * When use_trade_overrides flips off, strip trade_overrides.
  if (key === "workmanship_offered" && value !== "yes") {
    delete next.workmanship_period;
    delete next.custom_workmanship_period;
    delete next.coverage_start;
    delete next.workmanship_coverage;
    delete next.workmanship_exclusions;
  }
  if (key === "workmanship_period" && value !== "custom") {
    delete next.custom_workmanship_period;
  }
  if (key === "service_contact_rule" && value !== "specific") {
    delete next.specific_service_contact;
  }
  if (key === "use_trade_overrides" && value !== true) {
    delete next.trade_overrides;
  }
  return next;
};

// Slice 2C5B · Multi-select checkbox reducer for Contract's jobsite_access
// and material_selections. Enforces the mutually-exclusive rule for
// `none_specific` on jobsite_access (selecting it clears everything else,
// selecting anything else clears `none_specific`).
export const toggleMultiSelect = (bag, key, value, opts = {}) => {
  const next = { ...(bag || {}) };
  const cur = Array.isArray(next[key]) ? [...next[key]] : [];
  const mutuallyExclusive = opts.mutuallyExclusive || null;
  const has = cur.includes(value);
  let updated;
  if (has) {
    updated = cur.filter((v) => v !== value);
  } else if (mutuallyExclusive && value === mutuallyExclusive) {
    // Selecting the exclusive option → collapse to just that.
    updated = [mutuallyExclusive];
  } else if (mutuallyExclusive && cur.includes(mutuallyExclusive)) {
    // Selecting anything else clears the exclusive option.
    updated = cur.filter((v) => v !== mutuallyExclusive).concat(value);
  } else {
    updated = cur.concat(value);
  }
  // Deduplicate defensively.
  next[key] = Array.from(new Set(updated));
  // Slice 2C5B Pass 2 · Invoice card-fee cleanup.
  //   When the contractor deselects "card" from payment_methods, the
  //   card_fee_handling answer is stale and must be dropped so it never
  //   leaks into the generated recommendation or the saved payload.
  if (key === "payment_methods" && !next[key].includes("card")) {
    delete next.card_fee_handling;
  }
  return next;
};

export const toggleNote = (bag, noteKey) => {
  const next = { ...(bag || {}) };
  next[noteKey] = !next[noteKey];
  return next;
};

// Slice 2C5C Pass 2 · Warranty simple trade-override helpers.
//   trade_overrides is a small array of `{trade, period, custom_period?}`.
//   These reducers enforce: only one override per trade, no duplicates,
//   custom_period allowed only when period='custom'. All state stays
//   local until the contractor presses Use recommended.
export const addTradeOverride = (bag, trade, period, custom = null) => {
  const next = { ...(bag || {}) };
  const cur = Array.isArray(next.trade_overrides) ? [...next.trade_overrides] : [];
  const t = (trade || "").trim();
  if (!t) return next;
  if (cur.some((o) => o.trade === t)) return next;   // no duplicate trades
  const entry = { trade: t, period };
  if (period === "custom" && custom && String(custom).trim()) {
    entry.custom_period = String(custom).trim();
  }
  next.trade_overrides = [...cur, entry];
  return next;
};
export const updateTradeOverride = (bag, trade, period, custom = null) => {
  const next = { ...(bag || {}) };
  const cur = Array.isArray(next.trade_overrides) ? [...next.trade_overrides] : [];
  const idx = cur.findIndex((o) => o.trade === trade);
  if (idx < 0) return next;
  const entry = { trade: cur[idx].trade, period };
  if (period === "custom" && custom && String(custom).trim()) {
    entry.custom_period = String(custom).trim();
  }
  cur[idx] = entry;
  next.trade_overrides = cur;
  return next;
};
export const removeTradeOverride = (bag, trade) => {
  const next = { ...(bag || {}) };
  const cur = Array.isArray(next.trade_overrides) ? [...next.trade_overrides] : [];
  next.trade_overrides = cur.filter((o) => o.trade !== trade);
  return next;
};

// --- Answer-change reconciliation ------------------------------------------
// Compare two family-specific answer bags and return whether any change
// would alter the generated wording. Used to trigger the "Answer changed
// after customized wording" reconciliation choice.
const PROPOSAL_ANSWER_KEYS = [
  "tone",
  "deposit_kind", "deposit_percent", "deposit_amount", "deposit_due",
  "final_due", "final_due_custom",
  "late_fee", "late_fee_amount", "late_fee_custom",
  "notes_change_order", "notes_warranty_separate",
];
const CONTRACT_ANSWER_KEYS = [
  "tone", "permits", "concealed_conditions",
  "jobsite_access", "scheduling", "material_selections", "cleanup",
];
const INVOICE_ANSWER_KEYS = [
  "tone",
  "billing_approach", "payment_due",
  "payment_methods", "card_fee_handling",
  "overdue_handling", "footer_items",
  "retainage_kind", "retainage_percent",
];
const CUSTOMER_UPDATE_ANSWER_KEYS = [
  "tone", "milestones", "delivery_method",
  "recipient_rule", "language_rule", "share_items",
];
const CLOSEOUT_ANSWER_KEYS = [
  "tone", "package_items", "draft_trigger",
  "walkthrough_rule", "punch_list_rule",
  "acknowledgment_rule", "photo_rule",
];
const WARRANTY_ANSWER_KEYS = [
  "tone", "workmanship_offered", "workmanship_period",
  "custom_workmanship_period", "coverage_start",
  "workmanship_coverage", "workmanship_exclusions",
  "manufacturer_handling", "service_contact_rule",
  "specific_service_contact", "request_information",
  "use_trade_overrides", "trade_overrides",
];
const FAMILY_ANSWER_KEYS_MAP = {
  proposal:        PROPOSAL_ANSWER_KEYS,
  contract:        CONTRACT_ANSWER_KEYS,
  invoice:         INVOICE_ANSWER_KEYS,
  customer_update: CUSTOMER_UPDATE_ANSWER_KEYS,
  closeout:        CLOSEOUT_ANSWER_KEYS,
  warranty:        WARRANTY_ANSWER_KEYS,
};

const _sameArray = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return a === b;
  if (a.length !== b.length) return false;
  // Slice 2C5C Pass 2 · trade_overrides is an array of small objects
  // (`{trade, period, custom_period?}`). Serialize each element with
  // stable key order, then sort element strings before joining.
  const _ser = (x) => {
    if (x && typeof x === "object" && !Array.isArray(x)) {
      const keys = Object.keys(x).sort();
      return JSON.stringify(keys.map((k) => [k, x[k]]));
    }
    return JSON.stringify(x);
  };
  const sa = a.map(_ser).sort().join("\u0001");
  const sb = b.map(_ser).sort().join("\u0001");
  return sa === sb;
};

export const answersDifferMeaningfully = (a = {}, b = {}, family = "proposal") => {
  const keys = FAMILY_ANSWER_KEYS_MAP[family] || PROPOSAL_ANSWER_KEYS;
  for (const k of keys) {
    const av = a[k];
    const bv = b[k];
    if (Array.isArray(av) || Array.isArray(bv)) {
      if (!_sameArray(av || [], bv || [])) return true;
      continue;
    }
    // Treat missing / undefined as equivalent.
    const aNorm = av === undefined ? null : av;
    const bNorm = bv === undefined ? null : bv;
    if (aNorm !== bNorm) return true;
  }
  return false;
};

// --- Compare-changes helper -------------------------------------------------
// Return a simple field-by-field diff bag consumed by the Compare-changes
// view. Each entry describes the customized value the contractor already
// saved and the newly-generated value from the updated answers.
export const buildCompareEntries = (customizedBag, generatedBag, allowlist) => {
  const out = [];
  for (const key of allowlist) {
    const cur = (customizedBag && customizedBag[key]) || "";
    const nxt = (generatedBag  && generatedBag[key])  || "";
    if (cur !== nxt) out.push({ key, customized: cur, generated: nxt });
  }
  return out;
};

// --- API helpers (kept OUT of Step3DocumentDefaults.jsx so the locked
//     Slice 2C3 test that protects the Step 3 direct-PUT boundary remains
//     unchanged) ---
//
// Both helpers accept an `apiClient` compatible with our shared axios
// wrapper (has `.get(url)` / `.put(url, body)`). Passing the client in
// keeps this module import-safe from Node test scripts.
export const loadDocumentGuidedSetup = async (apiClient) => {
  try {
    const r = await apiClient.get("/document-guided-setup");
    return r.data || { schema_version: 1, families: {} };
  } catch (_e) {
    return { schema_version: 1, families: {} };
  }
};

export const saveDocumentGuidedFamily = async (apiClient, familyKey, payload) => {
  const r = await apiClient.put(`/document-guided-setup/${familyKey}`, payload);
  return r.data;
};

// --- Slice 2C5A · Guided-draft localStorage shape ---------------------------
//
// The guided-question state is unsaved contractor work. It must survive
// refresh, isolate per-family, and cooperate with the existing Slice 2C2B
// multi-tab conflict controls used by the paragraph editor. To avoid a
// second incompatible draft system we reuse the SAME localStorage envelope
// (per-family key, `updated_at` baseline, storage-event fan-out) with a
// distinct namespace prefix `guided:`.
export const guidedDraftKey = (userId, family) =>
  `jc.guided-draft.${userId || "anon"}.${family}`;

export const readGuidedDraft = (userId, family) => {
  try {
    const raw = localStorage.getItem(guidedDraftKey(userId, family));
    return raw ? JSON.parse(raw) : null;
  } catch (_e) { return null; }
};

export const writeGuidedDraft = (userId, family, draft) => {
  try {
    localStorage.setItem(guidedDraftKey(userId, family), JSON.stringify(draft));
  } catch (_e) { /* ignore quota */ }
};

export const clearGuidedDraft = (userId, family) => {
  try { localStorage.removeItem(guidedDraftKey(userId, family)); }
  catch (_e) { /* ignore */ }
};

// A draft is "dirty" when either the answers or the adjustments differ
// from the persisted baseline.
export const isGuidedDirty = (draft, baseline, family = "proposal") => {
  if (!draft) return false;
  const bAnswers = (baseline && baseline.answers) || {};
  const bAdjustments = (baseline && baseline.adjustments) || {};
  const dAdjustments = draft.adjustments || {};
  if (answersDifferMeaningfully(draft.answers || {}, bAnswers, family)) return true;
  // Slice 2C5B · Pass 2 · Invoice browser-QA fix — compare against the
  // baseline adjustments so an approved adjusted recommendation is
  // NOT dirty just because helper refinements are active. Historical
  // callers (Proposal, Contract) still work: their baseline.adjustments
  // is {} today, so any non-empty local adjustments remain "dirty".
  try {
    if (JSON.stringify(dAdjustments) !== JSON.stringify(bAdjustments)) return true;
  } catch (_e) {
    if (Object.keys(dAdjustments).length > 0) return true;
  }
  return false;
};

// The server-side `updated_at` on the family slice is the multi-tab
// conflict baseline. When another tab saves, the new updated_at broadcasts
// via a `storage` event and callers surface Load-latest / Keep editing /
// Overwrite-with-my-changes exactly like Slice 2C2B.
export const guidedBaselineFromRecord = (guidedRecord) => ({
  updated_at:  (guidedRecord && guidedRecord.updated_at)  || null,
  answers:     (guidedRecord && guidedRecord.answers)     || {},
  // Slice 2C5B · Pass 2 · hydrate approved helper adjustments so the
  // panel can reproduce the exact adjusted recommendation after refresh.
  adjustments: (guidedRecord && guidedRecord.adjustments) || {},
  wording_source: (guidedRecord && guidedRecord.wording_source) || null,
});


// ============================================================================
// Slice 2S · Spanish schema translations
// ----------------------------------------------------------------------------
// Compact translation table for contractor-visible schema copy:
//   [family][questionId].{prompt, choices{value:label}, conditionalInputs{key:{label,placeholder}}, conditionalChip:{prompt,choices{value:label}}, noneLabel, helpText}
// The canonical FAMILY_QUESTION_SCHEMAS above stays as the single source of
// truth for question IDs, answer values, branching, storage, and API. This
// table only carries display-language overlays and is consumed at render
// time by getFamilyQuestions(family, lang).
// ============================================================================
const _ES_TONE_CHOICES = {
  friendly: "Amistoso",
  professional: "Profesional",
  straightforward: "Directo",
};

const _ES_QUESTIONS = {
  proposal: {
    tone: { prompt: "¿Cómo debe sonar la introducción de tu propuesta?", choices: _ES_TONE_CHOICES },
    deposit: {
      prompt: "¿Requieres un anticipo?",
      choices: { none: "Sin anticipo", percent: "Porcentaje", fixed: "Monto fijo en dólares" },
      conditionalInputs: {
        deposit_percent: { label: "Porcentaje de anticipo" },
        deposit_amount:  { label: "Monto del anticipo" },
      },
      conditionalChip: {
        prompt: "¿Cuándo vence el anticipo?",
        choices: {
          on_approval: "Al aprobar la propuesta",
          before_materials: "Antes de pedir materiales",
          before_work: "Antes de comenzar el trabajo",
        },
      },
    },
    final_due: {
      prompt: "¿Cuándo vence el saldo restante?",
      choices: {
        on_completion: "Al terminar el trabajo",
        net_7: "En 7 días",
        net_15: "En 15 días",
        net_30: "En 30 días",
        custom: "Personalizado",
      },
      conditionalInputs: {
        final_due_custom: { label: "Describe el plazo", placeholder: "p. ej. en 10 días hábiles" },
      },
    },
    late_fee: {
      prompt: "¿Cobras un recargo por pago tardío?",
      choices: {
        none: "Sin recargo",
        one_point_five_percent: "1,5 % mensual",
        flat: "Monto fijo en dólares",
        custom: "Personalizado",
      },
      conditionalInputs: {
        late_fee_amount: { label: "Recargo fijo" },
        late_fee_custom: { label: "Describe tu política de pago tardío", placeholder: "p. ej. 5 % del saldo pendiente por mes" },
      },
    },
    notes: {
      prompt: "¿Qué notas estándar de propuesta se deben incluir?",
      choices: {
        notes_change_order: "Aprobación escrita de órdenes de cambio",
        notes_warranty_separate: "La información de garantía se entrega por separado",
      },
      noneLabel: "Ninguna",
    },
  },
  contract: {
    tone: { prompt: "¿Cómo debe sonar tu contrato?", choices: _ES_TONE_CHOICES },
    permits: {
      prompt: "¿Quién suele tramitar los permisos?",
      choices: { contractor: "Contratista", customer: "Cliente", depends: "Depende del trabajo", not_required: "Usualmente no se requieren permisos" },
    },
    concealed_conditions: {
      prompt: "¿Cómo se deben manejar las condiciones ocultas o imprevistas?",
      choices: {
        change_order: "Detener y obtener aprobación escrita de orden de cambio antes del trabajo adicional",
        emergency_notify: "Permitir trabajos de emergencia por seguridad y avisar al cliente",
        help_me_decide: "Ayúdame a decidir",
      },
    },
    jobsite_access: {
      prompt: "¿Qué debe proveer normalmente el cliente en la obra?",
      choices: {
        property_access: "Acceso a la propiedad",
        utilities: "Electricidad y agua",
        pets: "Mascotas resguardadas",
        valuables: "Objetos de valor retirados o protegidos",
        none_specific: "Nada específico por defecto",
      },
    },
    scheduling: {
      prompt: "¿Cómo se debe describir el cronograma?",
      choices: {
        estimated_may_change: "Las fechas estimadas pueden cambiar por clima, materiales, inspecciones o condiciones fuera del control del contratista",
        firm_unless_written: "Fechas firmes salvo cambio por escrito",
        help_me_decide: "Ayúdame a decidir",
      },
    },
    material_selections: {
      prompt: "¿Cómo se deben manejar los materiales, colores y selecciones de productos?",
      choices: {
        customer_approval: "El cliente aprueba las selecciones antes de ordenar",
        special_order_nonreturnable: "Los materiales de pedido especial pueden no ser retornables",
        substitutions_need_approval: "Los sustitutos comparables requieren aprobación del cliente",
      },
    },
    cleanup: {
      prompt: "¿Qué limpieza está normalmente incluida?",
      choices: {
        daily_and_final: "Limpieza diaria razonable y retiro final de escombros",
        final_only: "Solo limpieza final",
        disposal_billed: "La disposición se factura por separado cuando corresponda",
        depends: "Depende del trabajo",
      },
    },
  },
  invoice: {
    tone: { prompt: "¿Cómo deben sonar tus facturas?", choices: _ES_TONE_CHOICES },
    billing_approach: {
      prompt: "¿Cómo facturas normalmente a los clientes?",
      choices: {
        one_final: "Una factura final",
        deposit_then_final: "Anticipo y luego factura final",
        progress_milestone: "Facturas por avance o hitos",
        depends: "Depende del trabajo",
      },
    },
    payment_due: {
      prompt: "¿Cuándo vence normalmente el pago?",
      choices: {
        upon_receipt: "Al recibir la factura",
        on_completion: "Al terminar el trabajo",
        net_7: "En 7 días",
        net_15: "En 15 días",
        net_30: "En 30 días",
        use_approved_terms: "Usar los términos aprobados de propuesta o contrato",
      },
    },
    payment_methods: {
      prompt: "¿Cómo pueden pagarte los clientes?",
      choices: {
        check: "Cheque",
        cash: "Efectivo",
        card: "Tarjeta de crédito o débito",
        ach: "ACH o transferencia bancaria",
        financing: "Financiamiento",
        other: "Otro",
      },
    },
    card_fee_handling: {
      prompt: "¿Cómo se manejan las comisiones por procesamiento con tarjeta?",
      choices: {
        absorb: "Absorbemos la comisión",
        add_disclosed: "Agregamos una comisión divulgada donde esté permitido",
        no_cards: "No aceptamos pagos con tarjeta",
        per_job: "Decidir por trabajo",
      },
    },
    overdue_handling: {
      prompt: "¿Cómo se manejan los saldos vencidos?",
      choices: {
        use_approved_terms: "Usar los términos aprobados de pago tardío",
        friendly_reminder_only: "Enviar solo un recordatorio amable de pago",
        no_late_fee: "Sin recargo por pago tardío",
        help_me_decide: "Ayúdame a decidir",
      },
    },
    footer_items: {
      prompt: "¿Qué debe aparecer normalmente al pie de las facturas?",
      choices: {
        thank_customer: "Agradecer al cliente",
        contact_billing: "Pedir al cliente que nos contacte por dudas de facturación",
        show_prior_payments: "Mostrar los pagos ya recibidos",
        show_remaining_balance: "Mostrar claramente el saldo restante",
        payment_instructions: "Incluir instrucciones de pago",
        license_info: "Incluir información de licencia o registro",
      },
    },
    retainage_kind: {
      prompt: "¿Retienes normalmente un porcentaje del pago?",
      choices: {
        none: "Sin retención",
        percent: "Sí, ingresar un porcentaje",
        depends: "Depende del proyecto",
      },
      conditionalInputs: {
        retainage_percent: { label: "Porcentaje de retención" },
      },
    },
  },
  customer_update: {
    tone: { prompt: "¿Cómo deben sonar los avisos al cliente?", choices: _ES_TONE_CHOICES },
    milestones: {
      prompt: "¿Qué hitos deben preparar un borrador de aviso?",
      helpText: "Los hitos seleccionados preparan un borrador para revisión del contratista. Nada se envía automáticamente.",
      choices: {
        job_scheduled: "Trabajo agendado",
        work_started: "Trabajo iniciado",
        delay_or_schedule_change: "Retraso o cambio de agenda",
        inspection_update: "Actualización de inspección",
        customer_decision_needed: "Se requiere una decisión del cliente",
        change_order_update: "Actualización de orden de cambio",
        work_completed: "Trabajo terminado",
        invoice_or_payment_reminder: "Recordatorio de factura o pago",
        other: "Otro",
      },
    },
    delivery_method: {
      prompt: "¿Cómo se deben entregar los avisos aprobados normalmente?",
      helpText: "Es solo una preferencia de entrega futura. JobClose AI no envía avisos.",
      choices: {
        text: "Mensaje de texto",
        email: "Correo electrónico",
        text_and_email: "Texto y correo electrónico",
        customer_portal: "Portal del cliente",
        decide_each: "Decidir por cada aviso",
      },
    },
    recipient_rule: {
      prompt: "¿Quién debe recibir normalmente los avisos al cliente?",
      choices: {
        primary: "Contacto principal del cliente",
        all_approved: "Todos los contactos aprobados del cliente",
        choose_each: "Elegir por cada aviso",
      },
    },
    language_rule: {
      prompt: "¿Qué idioma debe usar JobClose AI para los borradores de aviso?",
      helpText: "Para 'Idioma preferido del cliente' o 'Elegir por cada aviso', el idioma real se resuelve al momento del trabajo. Esta nota es solo para la interfaz y no se guarda como texto para el cliente.",
      choices: {
        preferred: "Idioma preferido del cliente",
        en: "Inglés",
        es: "Español",
        choose_each: "Elegir por cada aviso",
      },
    },
    share_items: {
      prompt: "¿Qué puede incluir JobClose AI en un borrador de aviso al cliente?",
      helpText: "Solo pueden incluirse elementos aprobados por el contratista. El contratista revisa y aprueba cada envío o publicación.",
      choices: {
        approved_photos: "Fotos aprobadas del trabajo",
        approved_schedule: "Cronograma o tiempos aprobados",
        approved_documents: "Documentos aprobados para el cliente",
        nothing_automatic: "No adjuntar ni enlazar nada automáticamente",
      },
    },
  },
  closeout: {
    package_items: {
      prompt: "¿Qué se debe considerar normalmente para el paquete de cierre?",
      helpText: "Son solo preferencias futuras del paquete. Nada se adjunta, comparte ni envía automáticamente.",
      choices: {
        final_invoice_summary: "Factura final o resumen de pago",
        signed_contract_and_change_orders: "Contrato firmado y órdenes de cambio aprobadas",
        workmanship_warranty: "Garantía de mano de obra",
        manufacturer_warranty_info: "Información de garantía del fabricante",
        permit_final_inspection_docs: "Documentos de permiso o inspección final",
        approved_final_photos: "Fotos finales aprobadas",
        care_maintenance_instructions: "Instrucciones de cuidado o mantenimiento",
        nothing_automatic: "Nada agregado automáticamente",
      },
    },
    draft_trigger: {
      prompt: "¿Cuándo debe JobClose AI preparar un borrador de cierre?",
      helpText: "El borrador de cierre se prepara solo después de que exista información confirmada por el contratista. Las fechas del calendario no se tratan como finalización.",
      choices: {
        after_inspections: "Después de que se confirme el trabajo y las inspecciones requeridas",
        after_walkthrough: "Después de la revisión final o aceptación del cliente",
        after_punchlist: "Después de completar los pendientes del punch list",
        decide_each: "Decidir por cada trabajo",
      },
    },
    walkthrough_rule: {
      prompt: "¿Cómo manejas normalmente la revisión final?",
      choices: {
        always: "Siempre completar una revisión final",
        when_required: "Completarla cuando el trabajo lo requiera",
        none: "Sin revisión formal",
        decide_each: "Decidir por cada trabajo",
      },
    },
    punch_list_rule: {
      prompt: "¿Cómo se deben manejar los pendientes del punch list?",
      helpText: "El texto nunca dará a entender que un trabajo incompleto está terminado.",
      choices: {
        all_before_closeout: "Completar todos los pendientes antes del cierre",
        written_after_ok: "Permitir un punch list por escrito aprobado por el cliente después del cierre",
        none: "Sin proceso formal de punch list",
        decide_each: "Decidir por cada trabajo",
      },
    },
    acknowledgment_rule: {
      prompt: "¿Cómo debe el cliente reconocer la finalización?",
      helpText: "Es solo una preferencia futura. En este paso no se implementa recolección de firmas.",
      choices: {
        signature: "Firma del cliente",
        written: "Reconocimiento por escrito",
        optional: "Reconocimiento opcional",
        decide_each: "Decidir por cada trabajo",
      },
    },
    photo_rule: {
      prompt: "¿Cómo se deben manejar las fotos finales?",
      helpText: "Solo pueden referenciarse fotos aprobadas por el contratista.",
      choices: {
        before_and_after: "Fotos aprobadas de antes y después",
        final_only: "Solo fotos finales aprobadas",
        none: "Sin fotos por defecto",
        decide_each: "Decidir por cada trabajo",
      },
    },
    tone: { prompt: "¿Cómo debe sonar el texto de cierre?", choices: _ES_TONE_CHOICES },
  },
  warranty: {
    workmanship_offered: {
      prompt: "¿Ofreces normalmente una garantía de mano de obra?",
      choices: { yes: "Sí", no_default: "Sin garantía de mano de obra por defecto", decide_each: "Decidir por cada trabajo" },
    },
    workmanship_period: {
      prompt: "¿Cuál es tu período estándar de garantía de mano de obra?",
      helpText: "Se muestra solo cuando normalmente ofreces una garantía de mano de obra.",
      periodLabels: {
        "30_days": "30 días",
        "90_days": "90 días",
        "1_year": "1 año",
        "2_years": "2 años",
        "5_years": "5 años",
        custom: "Personalizado",
        decide_each: "Decidir por cada trabajo",
      },
      conditionalInputs: {
        custom_workmanship_period: { label: "Período personalizado", placeholder: "p. ej. 3 años en mano de obra de techo plano" },
      },
    },
    coverage_start: {
      prompt: "¿Cuándo debe comenzar la cobertura de la garantía de mano de obra?",
      helpText: "Las fechas del calendario por sí solas no se tratan como confirmación de finalización.",
      choices: {
        contractor_substantial: "Finalización sustancial confirmada por el contratista",
        contractor_final: "Finalización final confirmada por el contratista",
        customer_acknowledgment: "Reconocimiento de finalización por el cliente",
        decide_each: "Decidir por cada trabajo",
      },
    },
    workmanship_coverage: {
      prompt: "¿Qué debe cubrir la garantía estándar de mano de obra?",
      helpText: "Se requiere al menos una selección. \"Decidir por cada trabajo\" es mutuamente excluyente con las demás opciones.",
      choices: {
        defects_workmanship: "Defectos causados por nuestra mano de obra",
        repairs_covered_work: "Reparaciones del trabajo cubierto cuando se confirme un problema de mano de obra",
        decide_each: "Decidir cobertura para cada trabajo",
      },
    },
    workmanship_exclusions: {
      prompt: "¿Qué debe excluirse normalmente de la garantía de mano de obra?",
      helpText: "Se requiere al menos una selección. \"Decidir por cada trabajo\" es mutuamente excluyente con las demás opciones.",
      choices: {
        manufacturer_or_product_defects: "Defectos del fabricante o del producto",
        normal_wear_and_tear: "Desgaste normal por uso",
        misuse_abuse_neglect: "Daños por mal uso, abuso, negligencia o falta de mantenimiento",
        weather_disasters_outside_forces: "Daños por clima, desastres o fuerzas externas",
        altered_or_repaired_by_others: "Trabajo alterado o reparado por otros",
        pre_existing_or_out_of_scope: "Condiciones preexistentes o trabajo fuera del alcance aprobado",
        decide_each: "Decidir exclusiones para cada trabajo",
      },
    },
    manufacturer_handling: {
      prompt: "¿Cómo se deben manejar las garantías del fabricante?",
      helpText: "La cobertura del fabricante se describe por separado y no se garantiza.",
      choices: {
        provide_available: "Proporcionar la información de garantía del fabricante disponible cuando aplique",
        refer_to_manufacturer: "Referir al cliente directamente al fabricante",
        none_default: "Sin información de garantía del fabricante por defecto",
        decide_each: "Decidir por cada trabajo",
      },
    },
    service_contact_rule: {
      prompt: "¿Cómo deben solicitar los clientes el servicio de garantía?",
      choices: {
        company_contact: "Contactar a nuestra empresa usando la información en el documento",
        specific: "Usar un contacto específico de garantía",
        decide_each: "Decidir por cada trabajo",
      },
      conditionalInputs: {
        specific_service_contact: { label: "Contacto específico de garantía", placeholder: "p. ej. Departamento de Garantía · (555) 555-1234 · garantia@acme.example" },
      },
    },
    request_information: {
      prompt: "¿Qué información deben proporcionar los clientes al solicitar servicio de garantía?",
      helpText: "Son solo instrucciones para el cliente. En este paso no se implementa un flujo de recepción de reclamos.",
      choices: {
        customer_and_project_name: "Nombre del cliente y del proyecto",
        description_of_concern: "Descripción de la inquietud",
        photos_when_available: "Fotos de la inquietud cuando estén disponibles",
        date_first_noticed: "Fecha en que se notó por primera vez",
        decide_each: "Decidir por cada trabajo",
      },
    },
    tone: { prompt: "¿Cómo debe sonar el texto de garantía?", choices: _ES_TONE_CHOICES },
  },
};

// Locale-aware schema getter. Returns the canonical FAMILY_QUESTION_SCHEMAS[family]
// when lang !== "es", and a Spanish-translated deep-clone when lang === "es".
// Question IDs, choice values, conditional-input keys, branching, and defaults
// are preserved byte-identically — only display strings are swapped.
export const getFamilyQuestions = (family, lang) => {
  const schema = FAMILY_QUESTION_SCHEMAS[family];
  if (!schema) return [];
  if (lang !== "es") return schema;
  const es = _ES_QUESTIONS[family] || {};
  return schema.map((q) => {
    const tq = es[q.id] || {};
    const next = { ...q };
    if (tq.prompt) next.prompt = tq.prompt;
    if (tq.helpText) next.helpText = tq.helpText;
    if (tq.noneLabel) next.noneLabel = tq.noneLabel;
    if (q.choices && tq.choices) {
      // Special handling for warranty_period: periodLabels overrides label
      if (family === "warranty" && q.id === "workmanship_period" && tq.periodLabels) {
        next.choices = q.choices.map((c) => ({ ...c, label: tq.periodLabels[c.value] || c.label }));
      } else {
        next.choices = q.choices.map((c) => ({ ...c, label: tq.choices[c.value] || c.label }));
      }
    }
    if (q.conditionalInputs && tq.conditionalInputs) {
      next.conditionalInputs = q.conditionalInputs.map((ci) => {
        const t = tq.conditionalInputs[ci.key] || {};
        return { ...ci, label: t.label || ci.label, placeholder: t.placeholder || ci.placeholder };
      });
    }
    if (q.conditionalChip && tq.conditionalChip) {
      const tc = tq.conditionalChip;
      const nextChip = { ...q.conditionalChip };
      if (tc.prompt) nextChip.prompt = tc.prompt;
      if (q.conditionalChip.choices && tc.choices) {
        nextChip.choices = q.conditionalChip.choices.map((c) => ({ ...c, label: tc.choices[c.value] || c.label }));
      }
      next.conditionalChip = nextChip;
    }
    return next;
  });
};
